/**
 * R45 / T2 — CARD REVIEW COUNT SHOWS THE NUMBER ONLY.
 * R50 — AND THAT NUMBER IS SHOWN IN PARENTHESES: "32" -> "(32)".
 *
 * The reviews app renders its bottom line from a configured template that currently reads
 * "{{reviews_count}} Reviews". The durable fix is that template, in the app's own admin —
 * it is recorded in the round's report as an owner action. This file is the theme-side
 * route the dispatch specifies when the setting is not available to us: the rendered text
 * node is trimmed to the number after the widget paints.
 *
 * WHY A TEXT REWRITE AND NOT CSS. Hiding the word with CSS would need the number and the
 * word to be separate elements; they are one text node, so there is nothing to hide
 * without hiding the count as well. The same applies to the parentheses: they cannot be
 * added as ::before/::after on that node, because the card surfaces that DO render a count
 * and the one that does not are different elements, and the two would then disagree.
 *
 * ONE FORMATTER, BOTH PATHS. A card gets its count one of two ways — the app renders one
 * and it is trimmed here, or the app renders none and one is injected here from the
 * product metafield. Both go through FORMAT below, so the two surfaces cannot drift apart
 * in shape. That is the whole reason it is a named function and not an inline template.
 *
 * IDEMPOTENT, AND IT SURVIVES RE-INIT. The app re-renders these rows on its own (and the
 * recommendations carousel explicitly re-initialises them), so this cannot be a one-shot
 * pass. Re-running over an already-formatted row is a no-op — neither pattern below can
 * match a string that starts with "(", so "(32)" can never become "((32))" — and a row
 * that never matched is never touched.
 *
 * ACCESSIBILITY IS UNAFFECTED. The count sits inside a button whose aria-label carries the
 * full sentence ("4.9 out 5 stars rating in total 32 reviews"). That label is a separate
 * attribute and is deliberately left alone, so screen readers keep the complete wording
 * while the visible chip shows the number the card has room for. The parentheses are
 * VISUAL TEXT ONLY and are never written into an accessible name (R45 rule).
 *
 * SCOPED TO THE STAR-RATINGS INSTANCE so no other reviews surface is reached.
 */
(() => {
  const INSTANCE = '1343804';
  // R46 / T1 — CARD SURFACES ONLY. The PDP buy-column row is the same widget instance but
  // deliberately keeps the spelled-out "N Reviews", replicating the live theme. Rooting at
  // product-card excludes it, and also covers the PDP recommendation carousel, whose cards
  // are product-card elements too.
  const CONTAINER = `product-card .yotpo-widget-instance[data-yotpo-instance-id="${INSTANCE}"]`;
  const COUNT = '.yotpo-sr-bottom-line-right-panel';

  // R50 — the one place the visible shape of a card count is decided.
  const FORMAT = (number) => `(${number})`;

  // "32 Reviews", "1 review", "1,204 reviews" -> capture the number, drop the word.
  const WORDED = /^\s*([\d][\d,.\s]*)\s*reviews?\s*$/i;
  // R50 — a count with no word after it. The app's own template is an owner-editable
  // setting (recorded since R45), so it can start emitting a bare "32" at any time without
  // a theme change. Without this branch that day would silently undo the parentheses.
  // Deliberately INTEGER-SHAPED ONLY: a decimal such as "4.9" is a SCORE, not a count, and
  // must not be wrapped if the app ever renders one into this panel.
  const BARE = /^\s*(\d{1,3}(?:,\d{3})+|\d+)\s*$/;
  // Neither pattern can match a leading "(", which is what makes re-runs safe.

  const trim = (root) => {
    const scope = root && root.querySelectorAll ? root : document;
    for (const panel of scope.querySelectorAll(`${CONTAINER} ${COUNT}`)) {
      const text = panel.textContent || '';
      const match = WORDED.exec(text) || BARE.exec(text);
      if (!match) continue;
      const wanted = FORMAT(match[1].trim());
      if (text.trim() === wanted) continue;
      panel.textContent = wanted;
    }
  };

  /**
   * R46 / T6 — GIVE A CARD ITS COUNT WHEN THE WIDGET DOES NOT RENDER ONE.
   *
   * On the PDP recommendation carousel the app picks a bottom-line layout that shows a
   * score and emits no count element at all, so the row disagreed with the home cards no
   * matter how it was styled - there was nothing there to style. The count is recovered
   * from whichever source the surface actually has, in order of trustworthiness:
   *   1. the product's review metafield, published on the container by the card block;
   *   2. the summary button's accessible label, which spells out "in total N reviews".
   * If neither yields a number, nothing is injected and the row is left exactly as it was.
   *
   * R50 — the injected node is formatted by the SAME FORMAT function the trim path uses,
   * so the carousel and the home cards render the identical "(N)" shape.
   *
   * Idempotent and re-init safe: the injected node carries a marker, a container that
   * already has one is skipped, and a container whose own count element appears later is
   * left to the app. The same observer that trims the count drives this, so it survives the
   * recommendations carousel's explicit re-initialisation.
   */
  const MARK = 'data-eh-count';

  const countFromLabel = (root) => {
    const btn = root.querySelector('[aria-label]');
    if (!btn) return null;
    const m = /in total\s+([\d][\d,.]*)\s+reviews?/i.exec(btn.getAttribute('aria-label') || '');
    return m ? m[1].trim() : null;
  };

  const addMissingCounts = (scope) => {
    for (const container of scope.querySelectorAll(CONTAINER)) {
      if (container.querySelector(COUNT)) continue;          // the app rendered its own
      if (container.querySelector(`[${MARK}]`)) continue;    // we already added one
      if (!container.querySelector('svg')) continue;         // not painted yet

      const value = (container.getAttribute('data-eh-review-count') || '').trim()
        || countFromLabel(container);
      if (!value) continue;
      // R50 — A PRODUCT WITH NO REVIEWS STAYS AS IT IS. The metafield can be present and
      // zero, which would put a "(0)" on a card that shows nothing today. Requiring one
      // non-zero digit keeps that card untouched.
      if (!/[1-9]/.test(value)) continue;

      const row = container.querySelector('.yotpo-sr-bottom-line-summary') || container.firstElementChild;
      if (!row) continue;

      const span = document.createElement('span');
      span.setAttribute(MARK, '');
      span.className = 'eh-review-count';
      span.textContent = FORMAT(value);
      row.appendChild(span);
    }
  };

  let queued = false;
  const schedule = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      trim(document);
      addMissingCounts(document);
    });
  };

  const start = () => {
    trim(document);
    addMissingCounts(document);
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
